import React, { useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { Card } from 'primereact/card';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { handleGetRequest } from '../../service/GetTemplate';
import { handlePostRequest } from '../../service/PostTemplate';
import { handlePatchRequest } from '../../service/PatchTemplate';
import { handleDeleteRequest } from '../../service/DeleteTemplate';
import { endPoints } from '../../Config';
import PhotoUploader from '../../components/PhotoUploader';
import { useNavigate } from 'react-router-dom';
import { formatLocalizedDateTime, formatTimeFromMinutes } from '../../utilities/formatTime';
import { exportToExcel } from '../../utilities/exportToExcel';
import { ROUTE_OPTIONS } from '../../utilities/constants';
import { getPreferredLanguage } from '../../utilities/languagePreference';
import { translateUiText, translateUiToken } from '../../utilities/siteTranslations';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import {
    getAllFailureTypeOptions,
    getFailureCategoryOptions,
    getFailureCategoryPriority,
    getFailureTypeOptionsForMachineType,
    inferRepairMachineType
} from '../../utilities/repairOrderFailureOptions';
import './RepairOrders.css';

const FINAL_STATUS_OPTIONS = [
    { label: 'Fully Operational', value: 'Fully Operational' },
    { label: 'Operational with minor issue', value: 'Operational with minor issue' },
    { label: 'Requires Follow-up', value: 'Requires Follow-up' }
];

const MAX_CLOSE_PHOTOS = 10;
const MAX_CLOSE_TOTAL_BYTES = 5 * 1024 * 1024;
const MAX_CREATE_PHOTOS = 10;
const MAX_CREATE_PHOTO_BYTES = 1.5 * 1024 * 1024;
const MAX_CREATE_TOTAL_BYTES = 10 * 1024 * 1024;
const QR_REPAIR_ISSUE_SUMMARY = 'Machine issue reported via QR';
const MARGARITA_ALLOWED_EMAILS = ['operaciones@coffeevending.com.pa'];
const ORDER_TYPES = {
    REPAIR: 'repair',
    FOLLOW_UP: 'follow_up'
};

const toEndOfDayISOString = (date) => {
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay.toISOString();
};

const RepairOrders = () => {
    const [preferredLanguage, setPreferredLanguage] = useState(getPreferredLanguage());
    const [openOrders, setOpenOrders] = useState([]);
    const [openOrdersPagination, setOpenOrdersPagination] = useState({
        page: 1,
        limit: 15,
        total: 0,
        totalPages: 0
    });
    const [openOrdersSort, setOpenOrdersSort] = useState({
        sortBy: 'createdAt',
        sortOrder: 'desc'
    });
    const [closedOrders, setClosedOrders] = useState([]);
    const [closedOrdersPagination, setClosedOrdersPagination] = useState({
        page: 1,
        limit: 15,
        total: 0,
        totalPages: 0
    });
    const [openFollowUps, setOpenFollowUps] = useState([]);
    const [openFollowUpsPagination, setOpenFollowUpsPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
    });
    const [closedFollowUps, setClosedFollowUps] = useState([]);
    const [closedFollowUpsPagination, setClosedFollowUpsPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
    });
    const [weeklySummary, setWeeklySummary] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [closeDialogVisible, setCloseDialogVisible] = useState(false);
    const [createDialogVisible, setCreateDialogVisible] = useState(false);
    const [createDialogMode, setCreateDialogMode] = useState(ORDER_TYPES.REPAIR);
    const [assignDialogVisible, setAssignDialogVisible] = useState(false);
    const [savingCreate, setSavingCreate] = useState(false);
    const [savingAssign, setSavingAssign] = useState(false);
    const [machines, setMachines] = useState([]);
    const [technicians, setTechnicians] = useState([]);
    const [closeForm, setCloseForm] = useState({
        typeOfFailure: '',
        failureCategory: '',
        actionsTaken: '',
        finalStatus: '',
        photoUrls: []
    });
    const [createForm, setCreateForm] = useState({
        repairMachineType: '',
        machineId: '',
        technicianId: '',
        typeOfFailure: '',
        failureCategory: '',
        issueDetails: '',
        photoUrls: []
    });
    const [assignForm, setAssignForm] = useState({
        technicianId: ''
    });
    const [reportRange, setReportRange] = useState({
        from: null,
        to: null
    });
    const [openOrdersDateRange, setOpenOrdersDateRange] = useState({
        from: null,
        to: null
    });
    const [openRouteFilter, setOpenRouteFilter] = useState(null);
    const [closedOrdersDateRange, setClosedOrdersDateRange] = useState({
        from: null,
        to: null
    });
    const [closedCustomerFilter, setClosedCustomerFilter] = useState(null);
    const [technicianOpenScope, setTechnicianOpenScope] = useState('mine');
    const [unassignedOpenCount, setUnassignedOpenCount] = useState(0);
    const [monthlyRepairOrderCounts, setMonthlyRepairOrderCounts] = useState({
        completed: 0,
        total: 0
    });

    const dispatch = useDispatch();
    const user = JSON.parse(localStorage.getItem('userDetails') || '{}');
    const adminUser = user?.data?.adminUser || {};
    const normalizePermissionToken = (value) =>
        String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
    const roleUid = adminUser?.role?.uid;
    const roleToken = normalizePermissionToken(
        roleUid ||
        adminUser?.role?.title ||
        adminUser?.roleUid ||
        user?.data?.role?.uid ||
        user?.data?.role?.title ||
        user?.role?.uid ||
        user?.role?.title
    );
    const currentUserEmail = String(
        adminUser?.email ||
        adminUser?.user?.email ||
        user?.data?.email ||
        user?.email ||
        ''
    ).trim().toLowerCase();
    const isOperator = roleToken === 'operator';
    const isTechnician = roleToken === 'technician';
    const navigate = useNavigate();
    const canCreateManualTicket = Boolean(adminUser?._id);
    const canAssignTechnician = !isOperator;
    const canManageAssignments = ['support', 'admin', 'superadmin'].includes(roleToken);
    const canCloseTickets = !isOperator;
    const canDeleteOpenTickets =
        ['admin', 'superadmin'].includes(roleToken) ||
        MARGARITA_ALLOWED_EMAILS.includes(currentUserEmail);
    const t = (value) => translateUiText(value, preferredLanguage);

    const buildOrdersEndpoint = (params) =>
        isOperator ? endPoints.getMyRepairOrders(params) : endPoints.getRepairOrders(params);

    useEffect(() => {
        const syncPreferredLanguage = () => setPreferredLanguage(getPreferredLanguage());
        window.addEventListener('preferred-language-changed', syncPreferredLanguage);
        window.addEventListener('storage', syncPreferredLanguage);

        return () => {
            window.removeEventListener('preferred-language-changed', syncPreferredLanguage);
            window.removeEventListener('storage', syncPreferredLanguage);
        };
    }, []);

    const loadOpenOrders = async (page = 1, limit = 15, sortConfig = openOrdersSort) => {
        const params = new URLSearchParams();
        params.append('status', 'open');
        params.append('orderType', ORDER_TYPES.REPAIR);
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder);
        
        // Technicians see only their assigned repair orders
        if (isTechnician && adminUser?._id) {
            if (technicianOpenScope === 'unassigned') {
                params.append('unassignedOnly', 'true');
            } else {
                params.append('technicianId', adminUser._id);
            }
        }
        
        // Date filters for open orders (filter by creation date)
        if (openOrdersDateRange.from) {
            params.append('createdFrom', openOrdersDateRange.from.toISOString());
        }
        if (openOrdersDateRange.to) {
            params.append('createdTo', openOrdersDateRange.to.toISOString());
        }
        if (openRouteFilter) {
            params.append('route', openRouteFilter);
        }
        
        const response = await handleGetRequest(buildOrdersEndpoint(params.toString()), true, false, dispatch);
        if (response?.data) {
            setOpenOrders(response.data.data || []);
            setOpenOrdersPagination(response.data.pagination || {
                page: 1,
                limit: 15,
                total: 0,
                totalPages: 0
            });
        }
    };

    const loadClosedOrders = async (page = 1, limit = 15) => {
        const params = new URLSearchParams();
        params.append('status', 'completed');
        params.append('orderType', ORDER_TYPES.REPAIR);
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        params.append('sortBy', 'closedAt');
        params.append('sortOrder', 'desc'); // Newest first
        
        // Technicians see only their assigned repair orders
        if (!isOperator && roleUid === 'technician' && adminUser?._id) {
            params.append('technicianId', adminUser._id);
        }
        
        // Date filters for closed orders
        if (closedOrdersDateRange.from) {
            params.append('closedFrom', closedOrdersDateRange.from.toISOString());
        }
        if (closedOrdersDateRange.to) {
            params.append('closedTo', closedOrdersDateRange.to.toISOString());
        }
        if (closedCustomerFilter) {
            params.append('customerLocation', closedCustomerFilter);
        }
        
        const response = await handleGetRequest(buildOrdersEndpoint(params.toString()), true, false, dispatch);
        if (response?.data) {
            setClosedOrders(response.data.data || []);
            setClosedOrdersPagination(response.data.pagination || {
                page: 1,
                limit: 15,
                total: 0,
                totalPages: 0
            });
        }
    };

    const appendTechnicianScopeParams = (params) => {
        if (isTechnician && adminUser?._id) {
            if (technicianOpenScope === 'unassigned') {
                params.append('unassignedOnly', 'true');
            } else {
                params.append('technicianId', adminUser._id);
            }
        }
    };

    const loadOpenFollowUps = async (page = 1, limit = 10) => {
        const params = new URLSearchParams();
        params.append('status', 'open');
        params.append('orderType', ORDER_TYPES.FOLLOW_UP);
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        params.append('sortBy', 'createdAt');
        params.append('sortOrder', 'desc');
        appendTechnicianScopeParams(params);

        const response = await handleGetRequest(buildOrdersEndpoint(params.toString()), true, false, dispatch);
        if (response?.data) {
            setOpenFollowUps(response.data.data || []);
            setOpenFollowUpsPagination(response.data.pagination || {
                page: 1,
                limit: 10,
                total: 0,
                totalPages: 0
            });
        }
    };

    const loadClosedFollowUps = async (page = 1, limit = 10) => {
        const params = new URLSearchParams();
        params.append('status', 'completed');
        params.append('orderType', ORDER_TYPES.FOLLOW_UP);
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        params.append('sortBy', 'closedAt');
        params.append('sortOrder', 'desc');
        if (!isOperator && roleUid === 'technician' && adminUser?._id) {
            params.append('technicianId', adminUser._id);
        }

        const response = await handleGetRequest(buildOrdersEndpoint(params.toString()), true, false, dispatch);
        if (response?.data) {
            setClosedFollowUps(response.data.data || []);
            setClosedFollowUpsPagination(response.data.pagination || {
                page: 1,
                limit: 10,
                total: 0,
                totalPages: 0
            });
        }
    };

    const loadWeeklyReport = async () => {
        if (isOperator) return;
        const params = new URLSearchParams();
        if (reportRange.from) {
            params.append('from', reportRange.from.toISOString());
        }
        if (reportRange.to) {
            params.append('to', toEndOfDayISOString(reportRange.to));
        }
        const response = await handleGetRequest(endPoints.weeklyRepairReport(params.toString()), true, false, dispatch);
        if (response?.data) {
            setWeeklySummary(response.data);
        }
    };

    const loadMachines = async () => {
        const machineEndpoint = isOperator
            ? `${endPoints.getallMachine}?assignedOnly=true`
            : endPoints.getallMachine;
        const response = await handleGetRequest(machineEndpoint, true, false, dispatch);
        if (response?.data) {
            setMachines(response.data);
        }
    };

    const loadTechnicians = async () => {
        if (isOperator) {
            setTechnicians([]);
            return;
        }
        const response = await handleGetRequest(endPoints.getTechnicians, true, false, dispatch);
        if (response?.data) {
            setTechnicians(response.data);
        }
    };

    const loadUnassignedOpenCount = async () => {
        if (isOperator) {
            setUnassignedOpenCount(0);
            return;
        }
        const params = new URLSearchParams();
        params.append('status', 'open');
        params.append('orderType', ORDER_TYPES.REPAIR);
        params.append('unassignedOnly', 'true');
        params.append('page', '1');
        params.append('limit', '1');
        const response = await handleGetRequest(endPoints.getRepairOrders(params.toString()), false, false, dispatch);
        const total = response?.data?.pagination?.total || 0;
        setUnassignedOpenCount(total);
    };

    const loadMonthlyRepairOrderCounts = async () => {
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        const appendMonthlyScope = (params) => {
            params.append('orderType', ORDER_TYPES.REPAIR);
            params.append('page', '1');
            params.append('limit', '1');
            if (!isOperator && isTechnician && adminUser?._id) {
                params.append('technicianId', adminUser._id);
            }
        };

        const completedParams = new URLSearchParams();
        appendMonthlyScope(completedParams);
        completedParams.append('status', 'completed');
        completedParams.append('closedFrom', monthStart.toISOString());
        completedParams.append('closedBefore', nextMonthStart.toISOString());

        const totalParams = new URLSearchParams();
        appendMonthlyScope(totalParams);
        totalParams.append('createdFrom', monthStart.toISOString());
        totalParams.append('createdBefore', nextMonthStart.toISOString());

        const [completedResponse, totalResponse] = await Promise.all([
            handleGetRequest(buildOrdersEndpoint(completedParams.toString()), false, false, dispatch),
            handleGetRequest(buildOrdersEndpoint(totalParams.toString()), false, false, dispatch)
        ]);

        setMonthlyRepairOrderCounts({
            completed: completedResponse?.data?.pagination?.total || 0,
            total: totalResponse?.data?.pagination?.total || 0
        });
    };

    const exportOpenOrders = async () => {
        try {
            const params = new URLSearchParams();
            params.append('status', 'open');
            params.append('orderType', ORDER_TYPES.REPAIR);
            params.append('limit', '10000'); // Large limit to get all records
            params.append('sortBy', 'createdAt');
            params.append('sortOrder', 'desc');
            
            // Technicians see only their assigned repair orders
            if (isTechnician && adminUser?._id) {
                if (technicianOpenScope === 'unassigned') {
                    params.append('unassignedOnly', 'true');
                } else {
                    params.append('technicianId', adminUser._id);
                }
            }
            
            // Apply date filters if set
            if (openOrdersDateRange.from) {
                params.append('createdFrom', openOrdersDateRange.from.toISOString());
            }
            if (openOrdersDateRange.to) {
                params.append('createdTo', openOrdersDateRange.to.toISOString());
            }
            if (openRouteFilter) {
                params.append('route', openRouteFilter);
            }
            
            const response = await handleGetRequest(buildOrdersEndpoint(params.toString()), true, false, dispatch);
            if (response?.data?.data) {
                const exportData = response.data.data.map((order) => ({
                    'Order ID': order._id || '-',
                    'Order Code': order?._id ? `#${String(order._id).slice(-4).toUpperCase()}` : '-',
                    'Machine Device ID': order.machine?.serialNumber || order.machine?.deviceId || '-',
                    'Machine Location': order.machine?.location || '-',
                    'Created By': order.createdBy ? `${order.createdBy.firstName || ''} ${order.createdBy.lastName || ''}`.trim() || order.createdBy.email || '-' : '-',
                    'Technician': order.technician ? `${order.technician.firstName || ''} ${order.technician.lastName || ''}`.trim() : '-',
                    'Issue Details': getIssueDetailsFromComplaint(order.complaint),
                    'Failure Type': order.typeOfFailure || '-',
                    'Failure Category': order.failureCategory || '-',
                    'Failure Priority': order.failurePriority ?? getFailureCategoryPriority(order.failureCategory) ?? '-',
                    'Created At': formatLocalizedDateTime(order.createdAt, preferredLanguage),
                    'Time Elapsed': order.createdAt ? formatTimeFromMinutes(calculateTimeElapsed(order.createdAt), preferredLanguage) : '-',
                    'Status': 'Open'
                }));

                const dateFilter = openOrdersDateRange.from || openOrdersDateRange.to 
                    ? `_${openOrdersDateRange.from ? new Date(openOrdersDateRange.from).toISOString().split('T')[0] : 'all'}_to_${openOrdersDateRange.to ? new Date(openOrdersDateRange.to).toISOString().split('T')[0] : 'all'}`
                    : '';
                
                exportToExcel(exportData, `Open_Repair_Orders${dateFilter}`, null);
            }
        } catch (error) {
            console.error('Error exporting open orders:', error);
            alert(t('Failed to export open orders. Please try again.'));
        }
    };

    const exportClosedOrders = async () => {
        try {
            const params = new URLSearchParams();
            params.append('status', 'completed');
            params.append('orderType', ORDER_TYPES.REPAIR);
            params.append('limit', '10000'); // Large limit to get all records
            params.append('sortBy', 'closedAt');
            params.append('sortOrder', 'desc');
            
            // Technicians see only their assigned repair orders
            if (!isOperator && roleUid === 'technician' && adminUser?._id) {
                params.append('technicianId', adminUser._id);
            }
            
            // Apply date filters if set
            if (closedOrdersDateRange.from) {
                params.append('closedFrom', closedOrdersDateRange.from.toISOString());
            }
            if (closedOrdersDateRange.to) {
                params.append('closedTo', closedOrdersDateRange.to.toISOString());
            }
            if (closedCustomerFilter) {
                params.append('customerLocation', closedCustomerFilter);
            }
            
            const response = await handleGetRequest(buildOrdersEndpoint(params.toString()), true, false, dispatch);
            if (response?.data?.data) {
                const exportData = response.data.data.map((order) => ({
                    'Order ID': order._id || '-',
                    'Order Code': order?._id ? `#${String(order._id).slice(-4).toUpperCase()}` : '-',
                    'Machine Device ID': order.machine?.serialNumber || order.machine?.deviceId || '-',
                    'Machine Location': order.machine?.location || '-',
                    'Technician': order.technician ? `${order.technician.firstName || ''} ${order.technician.lastName || ''}`.trim() : '-',
                    'Issue Details': getIssueDetailsFromComplaint(order.complaint),
                    'Failure Type': order.typeOfFailure || '-',
                    'Failure Category': order.failureCategory || '-',
                    'Failure Priority': order.failurePriority ?? getFailureCategoryPriority(order.failureCategory) ?? '-',
                    'Final Status': order.finalStatus || '-',
                    'Actions Taken': order.actionsTaken || '-',
                    'Time Consumed': formatTimeFromMinutes(order.timeConsumedMinutes, preferredLanguage),
                    'Created At': formatLocalizedDateTime(order.createdAt, preferredLanguage),
                    'Closed At': formatLocalizedDateTime(order.closedAt, preferredLanguage),
                    'Status': 'Completed'
                }));

                const dateFilter = closedOrdersDateRange.from || closedOrdersDateRange.to 
                    ? `_${closedOrdersDateRange.from ? new Date(closedOrdersDateRange.from).toISOString().split('T')[0] : 'all'}_to_${closedOrdersDateRange.to ? new Date(closedOrdersDateRange.to).toISOString().split('T')[0] : 'all'}`
                    : '';
                
                exportToExcel(exportData, `Closed_Repair_Orders${dateFilter}`, null);
            }
        } catch (error) {
            console.error('Error exporting closed orders:', error);
            alert(t('Failed to export closed orders. Please try again.'));
        }
    };

    useEffect(() => {
        loadOpenOrders();
        loadClosedOrders();
        loadOpenFollowUps();
        loadClosedFollowUps();
        loadMachines();
        loadTechnicians();
        loadUnassignedOpenCount();
        loadMonthlyRepairOrderCounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reload open orders when date filter changes
    useEffect(() => {
        loadOpenOrders(openOrdersPagination.page, openOrdersPagination.limit);
        loadOpenFollowUps(openFollowUpsPagination.page, openFollowUpsPagination.limit);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openOrdersDateRange.from, openOrdersDateRange.to, openRouteFilter, technicianOpenScope]);

    // Reload closed orders when date filter changes
    useEffect(() => {
        loadClosedOrders(closedOrdersPagination.page, closedOrdersPagination.limit);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [closedOrdersDateRange.from, closedOrdersDateRange.to, closedCustomerFilter]);

    const openCloseDialog = (order) => {
        setSelectedOrder(order);
        setCloseForm({
            typeOfFailure: order?.typeOfFailure || '',
            failureCategory: order?.failureCategory || '',
            actionsTaken: '',
            finalStatus: '',
            photoUrls: []
        });
        setCloseDialogVisible(true);
    };

    const openCreateDialog = (mode = ORDER_TYPES.REPAIR) => {
        setCreateDialogMode(mode);
        setCreateForm({
            repairMachineType: '',
            machineId: '',
            technicianId: '',
            typeOfFailure: '',
            failureCategory: '',
            issueDetails: '',
            photoUrls: []
        });
        setCreateDialogVisible(true);
    };

    const openAssignDialog = (order) => {
        setSelectedOrder(order);
        setAssignForm({
            technicianId: order?.technician?._id || ''
        });
        setAssignDialogVisible(true);
    };

    const handleCloseOrderSubmit = async () => {
        if (!selectedOrder?._id) return;
        const photoUrls = (closeForm.photoUrls || []).filter((photo) => typeof photo === 'string' && photo.trim());
        const isFollowUp = selectedOrder?.orderType === ORDER_TYPES.FOLLOW_UP;
        if (photoUrls.length > MAX_CLOSE_PHOTOS) {
            toast.warn(t(`Maximum ${MAX_CLOSE_PHOTOS} photos allowed.`));
            return;
        }
        const payload = {
            actionsTaken: closeForm.actionsTaken,
            finalStatus: closeForm.finalStatus,
            photoUrls,
            // Backward compatibility with any endpoint still expecting a single photo.
            photoUrl: photoUrls[0]
        };
        if (!isFollowUp) {
            payload.typeOfFailure = closeForm.typeOfFailure;
            payload.failureCategory = closeForm.failureCategory;
        }
        const response = await dispatch(handlePostRequest(payload, endPoints.closeRepairOrder(selectedOrder._id), true));
        if (response?.status === 200) {
            setCloseDialogVisible(false);
            setSelectedOrder(null);
            await loadOpenOrders(openOrdersPagination.page, openOrdersPagination.limit);
            await loadClosedOrders(closedOrdersPagination.page, closedOrdersPagination.limit);
            await loadOpenFollowUps(openFollowUpsPagination.page, openFollowUpsPagination.limit);
            await loadClosedFollowUps(closedFollowUpsPagination.page, closedFollowUpsPagination.limit);
            await loadUnassignedOpenCount();
            await loadMonthlyRepairOrderCounts();
        }
    };

    const handleCreateTicketSubmit = async () => {
        if (!createForm.machineId || !createForm.issueDetails.trim()) return;
        if (canAssignTechnician && !createForm.technicianId) return;
        if (createDialogMode === ORDER_TYPES.REPAIR && (!createForm.typeOfFailure || !createForm.failureCategory)) return;
        setSavingCreate(true);
        const selectedMachine = machines.find((machine) => machine._id === createForm.machineId);
        const inferredRepairMachineType =
            createForm.repairMachineType ||
            inferRepairMachineType(
                [
                    selectedMachine?.machine,
                    selectedMachine?.type,
                    selectedMachine?.name,
                    selectedMachine?.model
                ].filter(Boolean).join(' ')
            );
        const photoUrls = (createForm.photoUrls || []).filter((photo) => typeof photo === 'string' && photo.trim());
        const payload = {
            machineId: createForm.machineId,
            repairMachineType: inferredRepairMachineType,
            issueDetails: createForm.issueDetails.trim(),
            complaintType: createDialogMode === ORDER_TYPES.FOLLOW_UP ? 'follow_up' : 'technical',
            orderType: createDialogMode,
            photoUrls,
            photoUrl: photoUrls[0] || null
        };
        if (createDialogMode === ORDER_TYPES.REPAIR) {
            payload.typeOfFailure = createForm.typeOfFailure;
            payload.failureCategory = createForm.failureCategory;
        }
        if (canAssignTechnician && createForm.technicianId) {
            payload.technicianId = createForm.technicianId;
        }

        const response = await dispatch(handlePostRequest(payload, endPoints.createManualComplaint, true));
        setSavingCreate(false);

        if (response?.status === 201) {
            const createdOrderId = response?.data?.data?.repairOrder?._id;
            setCreateDialogVisible(false);
            await loadOpenOrders(openOrdersPagination.page, openOrdersPagination.limit);
            await loadClosedOrders(closedOrdersPagination.page, closedOrdersPagination.limit);
            await loadOpenFollowUps(openFollowUpsPagination.page, openFollowUpsPagination.limit);
            await loadClosedFollowUps(closedFollowUpsPagination.page, closedFollowUpsPagination.limit);
            await loadUnassignedOpenCount();
            await loadMonthlyRepairOrderCounts();
            if (createdOrderId) {
                navigate(`/repair-orders/${createdOrderId}`);
            }
        }
    };

    const handleClaimOrder = async (rowData) => {
        if (!isTechnician || !adminUser?._id || !rowData?._id) return;
        const payload = { technicianId: adminUser._id };
        const response = await dispatch(
            handlePatchRequest(payload, endPoints.assignRepairTechnician(rowData._id), true)
        );
        if (response?.status === 200) {
            await loadOpenOrders(openOrdersPagination.page, openOrdersPagination.limit);
            await loadClosedOrders(closedOrdersPagination.page, closedOrdersPagination.limit);
            await loadOpenFollowUps(openFollowUpsPagination.page, openFollowUpsPagination.limit);
            await loadClosedFollowUps(closedFollowUpsPagination.page, closedFollowUpsPagination.limit);
            await loadUnassignedOpenCount();
            await loadMonthlyRepairOrderCounts();
        }
    };

    const handleAssignOrder = async () => {
        if (!canManageAssignments || !selectedOrder?._id || !assignForm.technicianId) return;
        setSavingAssign(true);
        const payload = { technicianId: assignForm.technicianId };
        const response = await dispatch(
            handlePatchRequest(payload, endPoints.assignRepairTechnician(selectedOrder._id), true)
        );
        setSavingAssign(false);
        if (response?.status === 200) {
            setAssignDialogVisible(false);
            setSelectedOrder(null);
            await loadOpenOrders(openOrdersPagination.page, openOrdersPagination.limit);
            await loadClosedOrders(closedOrdersPagination.page, closedOrdersPagination.limit);
            await loadOpenFollowUps(openFollowUpsPagination.page, openFollowUpsPagination.limit);
            await loadClosedFollowUps(closedFollowUpsPagination.page, closedFollowUpsPagination.limit);
            await loadUnassignedOpenCount();
            await loadMonthlyRepairOrderCounts();
        }
    };

    const handleDeleteOpenOrder = (rowData) => {
        if (!canDeleteOpenTickets || rowData?.status !== 'open' || !rowData?._id) return;

        const ticketLabel = `#${String(rowData._id).slice(-4).toUpperCase()}`;
        confirmDialog({
            message: `${t('Are you sure you want to delete')} ${ticketLabel}? ${t('This action cannot be undone.')}`,
            header: t('Confirm Deletion'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            acceptLabel: t('Delete'),
            rejectLabel: t('Cancel'),
            accept: async () => {
                const response = await dispatch(handleDeleteRequest(endPoints.deleteRepairOrder(rowData._id), true, true));
                if (response?.status === 200) {
                    await loadOpenOrders(openOrdersPagination.page, openOrdersPagination.limit);
                    await loadClosedOrders(closedOrdersPagination.page, closedOrdersPagination.limit);
                    await loadOpenFollowUps(openFollowUpsPagination.page, openFollowUpsPagination.limit);
                    await loadClosedFollowUps(closedFollowUpsPagination.page, closedFollowUpsPagination.limit);
                    await loadUnassignedOpenCount();
                    await loadMonthlyRepairOrderCounts();
                }
            }
        });
    };

    const priorityBodyTemplate = (rowData) => {
        const priority = rowData.failurePriority ?? getFailureCategoryPriority(rowData.failureCategory);
        return priority ? <span className="font-bold">{priority}/10</span> : '-';
    };

    const handleOpenOrdersSort = (event) => {
        const nextSort = {
            sortBy: event.sortField || 'createdAt',
            sortOrder: event.sortOrder === 1 ? 'asc' : 'desc'
        };
        setOpenOrdersSort(nextSort);
        loadOpenOrders(1, openOrdersPagination.limit, nextSort);
    };

    const minutesBodyTemplate = (rowData) => {
        return formatTimeFromMinutes(rowData.timeConsumedMinutes, preferredLanguage);
    };

    // Same elapsed-time logic used in Open Complaints table
    const calculateTimeElapsed = (createdAt) => {
        if (!createdAt) return null;
        const now = new Date();
        const created = new Date(createdAt);
        const diffMs = now - created;
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        return diffMinutes;
    };

    const getTimeColor = (minutes) => {
        if (minutes < 120) return '#22c55e'; // green < 2 hours
        if (minutes < 480) return '#eab308'; // yellow 2-8 hours
        if (minutes < 1440) return '#f97316'; // orange 8-24 hours
        return '#ef4444'; // red > 24 hours
    };

    const timeElapsedBodyTemplate = (rowData) => {
        const minutes = calculateTimeElapsed(rowData?.createdAt);
        if (minutes === null) return '-';
        const color = getTimeColor(minutes);
        const formatted = formatTimeFromMinutes(minutes, preferredLanguage);
        return (
            <span style={{ color, fontWeight: 'bold' }}>
                {formatted}
            </span>
        );
    };

    const estimateDataUrlSize = (dataUrl) => {
        if (!dataUrl || typeof dataUrl !== 'string') return 0;
        const parts = dataUrl.split(',');
        if (parts.length < 2) return 0;
        return Math.ceil((parts[1].length * 3) / 4);
    };

    const getTotalPhotoBytes = (photos = []) =>
        photos.reduce((sum, photo) => sum + estimateDataUrlSize(photo), 0);

    const addClosePhoto = (photoValue) => {
        if (!photoValue || typeof photoValue !== 'string') return;
        setCloseForm((prev) => {
            if (prev.photoUrls.length >= MAX_CLOSE_PHOTOS) {
                toast.warn(t(`Maximum ${MAX_CLOSE_PHOTOS} photos allowed.`));
                return prev;
            }
            const nextPhotos = [...prev.photoUrls, photoValue];
            const totalBytes = getTotalPhotoBytes(nextPhotos);
            if (totalBytes > MAX_CLOSE_TOTAL_BYTES) {
                toast.warn(t('Total photo size limit reached. Remove one photo or upload smaller images.'));
                return prev;
            }
            return {
                ...prev,
                photoUrls: nextPhotos
            };
        });
    };

    const addCreatePhoto = (photoValue) => {
        if (!photoValue || typeof photoValue !== 'string') return;
        setCreateForm((prev) => {
            if ((prev.photoUrls || []).length >= MAX_CREATE_PHOTOS) {
                toast.warn(t(`Maximum ${MAX_CREATE_PHOTOS} photos allowed.`));
                return prev;
            }
            const nextPhotos = [...(prev.photoUrls || []), photoValue];
            const totalBytes = getTotalPhotoBytes(nextPhotos);
            if (totalBytes > MAX_CREATE_TOTAL_BYTES) {
                toast.warn(t('Total photo size limit reached. Remove one photo or upload smaller images.'));
                return prev;
            }
            return {
                ...prev,
                photoUrls: nextPhotos
            };
        });
    };

    const machineBodyTemplate = (rowData) => {
        const machine = rowData.machine || {};
        return (
            <div>
                <div className="font-bold">{machine.serialNumber || machine.deviceId || '-'}</div>
                <small className="text-500">{machine.location}</small>
            </div>
        );
    };

    const getIssueDetailsFromComplaint = (complaintData) => {
        const storedSummary = complaintData?.issueSummary?.trim();
        if (storedSummary) return storedSummary;

        if (!complaintData?.reportedBy && complaintData?.levelOneQuestion) {
            return QR_REPAIR_ISSUE_SUMMARY;
        }

        if (!complaintData?.chat || !Array.isArray(complaintData.chat)) return '-';
        const panelIssueMessage = complaintData.chat.find(
            (entry) =>
                entry &&
                entry.from === 'panel' &&
                !entry.isImage &&
                typeof entry.text === 'string' &&
                entry.text.trim().length > 0
        );
        if (panelIssueMessage?.text) return panelIssueMessage.text;

        // Fallback for older WhatsApp-created tickets:
        // pick the latest meaningful non-image text message.
        const fallback = [...complaintData.chat]
            .reverse()
            .find((entry) => {
                const text = entry?.text;
                if (!entry || entry.isImage || typeof text !== 'string') return false;
                const trimmed = text.trim();
                if (!trimmed) return false;
                // Avoid menu option-only inputs like "1", "2", etc.
                if (/^\d{1,2}$/.test(trimmed)) return false;
                return true;
            });

        return fallback?.text || '-';
    };

    const orderCodeBodyTemplate = (rowData) => {
        const fullId = rowData?._id;
        if (!fullId || typeof fullId !== 'string') return '-';
        return <span title={fullId}>{`#${fullId.slice(-4).toUpperCase()}`}</span>;
    };

    const issueDetailsBodyTemplate = (rowData) => {
        const issueDetails = getIssueDetailsFromComplaint(rowData?.complaint);
        if (!issueDetails || issueDetails === '-') return '-';
        const maxChars = 120;
        const shortText =
            issueDetails.length > maxChars
                ? `${issueDetails.slice(0, maxChars)}...`
                : issueDetails;
        return <span title={issueDetails}>{shortText}</span>;
    };

    const technicianBodyTemplate = (rowData) => {
        const tech = rowData.technician || {};
        if (!tech.firstName && !tech.lastName) return '-';
        return `${tech.firstName || ''} ${tech.lastName || ''}`.trim();
    };

    const createdByBodyTemplate = (rowData) => {
        const createdBy = rowData.createdBy || {};
        const fullName = `${createdBy.firstName || ''} ${createdBy.lastName || ''}`.trim();
        if (fullName) return fullName;
        if (createdBy.email) return createdBy.email;
        return '-';
    };

    const actionsBodyTemplate = (rowData) => {
        const assignedTechId = rowData?.technician?._id?.toString?.() || null;
        const isAssignedToMe = assignedTechId && assignedTechId === adminUser?._id;
        const canTechnicianClose = !isTechnician || isAssignedToMe;
        return (
            <div className="flex gap-2">
                <Button label={t('View')} icon="pi pi-eye" className="p-button-sm p-button-rounded p-button-text" onClick={() => navigate(`/repair-orders/${rowData._id}`)} />
                {canManageAssignments && rowData.status === 'open' && (
                    <Button
                        label={assignedTechId ? t('Reassign') : t('Assign')}
                        icon="pi pi-user-edit"
                        className="p-button-info p-button-sm"
                        onClick={() => openAssignDialog(rowData)}
                    />
                )}
                {isTechnician && rowData.status === 'open' && !assignedTechId && (
                    <Button
                        label={t('Claim')}
                        icon="pi pi-user-plus"
                        className="p-button-warning p-button-sm"
                        onClick={() => handleClaimOrder(rowData)}
                    />
                )}
                {canCloseTickets && canTechnicianClose && rowData.status === 'open' && <Button label={t('Close')} icon="pi pi-check" className="p-button-success p-button-sm" onClick={() => openCloseDialog(rowData)} />}
                {canDeleteOpenTickets && rowData.status === 'open' && (
                    <Button
                        icon="pi pi-trash"
                        className="p-button-danger p-button-sm p-button-rounded"
                        onClick={() => handleDeleteOpenOrder(rowData)}
                        tooltip={t('Delete')}
                        tooltipOptions={{ position: 'top' }}
                        aria-label={t('Delete')}
                    />
                )}
                {rowData.status === 'completed' && <span className="text-500 text-xs">{t('Closed')}</span>}
            </div>
        );
    };

    const renderCloseDialogFooter = () => {
        const hasAtLeastOnePhoto = Array.isArray(closeForm.photoUrls) && closeForm.photoUrls.length > 0;
        const isFollowUp = selectedOrder?.orderType === ORDER_TYPES.FOLLOW_UP;
        const missingRepairFields = !isFollowUp && (!closeForm.typeOfFailure || !closeForm.failureCategory);
        const isDisabled = missingRepairFields || !closeForm.actionsTaken || !closeForm.finalStatus || !hasAtLeastOnePhoto;
        return (
            <div className="flex justify-content-end gap-2">
                <Button label={t('Cancel')} icon="pi pi-times" className="p-button-text" onClick={() => setCloseDialogVisible(false)} />
                <Button label={t('Save & Close')} icon="pi pi-check" className="p-button-success" onClick={handleCloseOrderSubmit} disabled={isDisabled} />
            </div>
        );
    };

    const renderCreateDialogFooter = () => {
        const isDisabled =
            !createForm.machineId ||
            !createForm.issueDetails.trim() ||
            (canAssignTechnician && !createForm.technicianId) ||
            (createDialogMode === ORDER_TYPES.REPAIR && (!createForm.typeOfFailure || !createForm.failureCategory)) ||
            savingCreate;
        return (
            <div className="repair-order-create-dialog-actions flex justify-content-end gap-2">
                <Button label={t('Cancel')} icon="pi pi-times" className="p-button-text" onClick={() => setCreateDialogVisible(false)} disabled={savingCreate} />
                <Button
                    label={createDialogMode === ORDER_TYPES.FOLLOW_UP ? t('Create Follow-up') : t('Create Ticket')}
                    icon="pi pi-plus"
                    className="p-button-primary"
                    onClick={handleCreateTicketSubmit}
                    disabled={isDisabled}
                    loading={savingCreate}
                />
            </div>
        );
    };

    const renderAssignDialogFooter = () => {
        const currentTechnicianId = selectedOrder?.technician?._id || '';
        const isDisabled =
            !assignForm.technicianId ||
            savingAssign ||
            assignForm.technicianId === currentTechnicianId;

        return (
            <div className="flex justify-content-end gap-2">
                <Button
                    label={t('Cancel')}
                    icon="pi pi-times"
                    className="p-button-text"
                    onClick={() => setAssignDialogVisible(false)}
                    disabled={savingAssign}
                />
                <Button
                    label={currentTechnicianId ? t('Update Assignment') : t('Assign Technician')}
                    icon="pi pi-check"
                    className="p-button-primary"
                    onClick={handleAssignOrder}
                    disabled={isDisabled}
                    loading={savingAssign}
                />
            </div>
        );
    };

    const machineOptions = machines.map((machine) => ({
        label: [
            machine.machine || machine.name || t('Unnamed Machine'),
            machine.type || t('Unknown Type'),
            machine.location || t('No location'),
            machine.serialNumber || machine.deviceId || machine.uuid || t('No ID')
        ].join(' - '),
        value: machine._id
    }));

    const getMachineRepairMachineType = (machine) =>
        inferRepairMachineType(
            [
                machine?.machine,
                machine?.type,
                machine?.name,
                machine?.model
            ].filter(Boolean).join(' ')
        );

    const getOrderRepairMachineType = (order) =>
        order?.repairMachineType ||
        order?.complaint?.manualIntake?.repairMachineType ||
        getMachineRepairMachineType(order?.machine);

    const selectedOrderRepairMachineType = getOrderRepairMachineType(selectedOrder);
    const selectedCreateMachine = machines.find((machine) => machine._id === createForm.machineId);
    const selectedCreateRepairMachineType =
        createForm.repairMachineType || getMachineRepairMachineType(selectedCreateMachine);
    const selectedCreateRepairMachineTypeLabel =
        selectedCreateRepairMachineType === 'coffee'
            ? t('Máquina de Café')
            : selectedCreateRepairMachineType === 'snack'
                ? t('Máquina de Snack')
                : '';
    const isClosingFollowUp = selectedOrder?.orderType === ORDER_TYPES.FOLLOW_UP;
    const localizedCreateFailureTypeOptions = (
        selectedCreateRepairMachineType
            ? getFailureTypeOptionsForMachineType(selectedCreateRepairMachineType)
            : getAllFailureTypeOptions()
    )
        .filter((item) => item.value !== 'General')
        .map((item) => ({ ...item, label: t(item.label) }));
    const localizedCreateFailureCategoryOptions = getFailureCategoryOptions(
        selectedCreateRepairMachineType,
        createForm.typeOfFailure
    ).map((item) => ({ ...item, label: t(item.label) }));
    const selectedCreateFailurePriority = getFailureCategoryPriority(createForm.failureCategory);

    const closedCustomerOptions = Array.from(
        machines.reduce((map, machine) => {
            const customerLocation = String(machine.location || '').trim();
            if (!customerLocation || map.has(customerLocation)) return map;
            map.set(customerLocation, {
                label: customerLocation,
                value: customerLocation
            });
            return map;
        }, new Map()).values()
    ).sort((left, right) => left.label.localeCompare(right.label));

    const technicianOptions = technicians.map((tech) => ({
        label: `${tech.firstName || ''} ${tech.lastName || ''}`.trim() || tech.email || t('Unknown Technician'),
        value: tech._id
    }));

    const localizedFailureTypeOptions = (
        selectedOrderRepairMachineType
            ? getFailureTypeOptionsForMachineType(selectedOrderRepairMachineType)
            : getAllFailureTypeOptions()
    ).map((item) => ({ ...item, label: t(item.label) }));
    const localizedFinalStatusOptions = FINAL_STATUS_OPTIONS.map((item) => ({ ...item, label: t(item.label) }));
    const localizedFailureCategoryOptions = getFailureCategoryOptions(selectedOrderRepairMachineType, closeForm.typeOfFailure).map((item) => ({ ...item, label: t(item.label) }));
    const selectedFailurePriority = getFailureCategoryPriority(closeForm.failureCategory);
    const localizedRouteOptions = ROUTE_OPTIONS.map((item) => ({
        ...item,
        label: item.value ? item.label : t(item.label)
    }));

    // Calculate total counts for summary cards
    const totalOpenCount = openOrdersPagination.total || openOrders.length;
    const totalClosedCount = closedOrdersPagination.total || closedOrders.length;
    const totalOpenFollowUps = openFollowUpsPagination.total || openFollowUps.length;
    const totalClosedFollowUps = closedFollowUpsPagination.total || closedFollowUps.length;
    const summaryColClass = isOperator ? 'col-12 md:col-4' : 'col-12 md:col-3';

    return (
        <div className="p-4">
            <ConfirmDialog />
            <div className="mb-4 flex justify-content-between align-items-start gap-3 flex-wrap">
                <div>
                    <h2>{t('Repair Orders')}</h2>
                    <p className="text-color-secondary">{t('Track, manage, and close repair orders for machine maintenance.')}</p>
                </div>
                {canCreateManualTicket && (
                    <div className="flex gap-2 flex-wrap justify-content-end">
                        <Button label={t('Create Follow-up')} icon="pi pi-calendar-plus" className="p-button-outlined" onClick={() => openCreateDialog(ORDER_TYPES.FOLLOW_UP)} />
                        <Button label={t('Create Ticket')} icon="pi pi-plus" className="p-button-primary" onClick={() => openCreateDialog(ORDER_TYPES.REPAIR)} />
                    </div>
                )}
            </div>

            <Dialog
                header={createDialogMode === ORDER_TYPES.FOLLOW_UP ? t('Create Follow-up') : t('Create Ticket')}
                visible={createDialogVisible}
                style={{ width: '42rem' }}
                className="repair-order-create-dialog"
                modal
                onHide={() => setCreateDialogVisible(false)}
                footer={renderCreateDialogFooter()}
            >
                <div className="grid p-fluid">
                    <div className="col-12">
                        <label className="block mb-2">
                            {t('Machine')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            value={createForm.machineId}
                            options={machineOptions}
                            onChange={(e) => {
                                const selectedMachine = machines.find((machine) => machine._id === e.value);
                                setCreateForm((prev) => ({
	                                    ...prev,
	                                    machineId: e.value,
	                                    repairMachineType: getMachineRepairMachineType(selectedMachine),
	                                    typeOfFailure: '',
	                                    failureCategory: ''
	                                }));
	                            }}
                            placeholder={t('Select machine')}
                            className="w-full"
                            filter
                        />
                        {selectedCreateRepairMachineTypeLabel && (
                            <small className="text-500 block mt-2">
                                {t('Detected machine type:')} {selectedCreateRepairMachineTypeLabel}
                            </small>
                        )}
                    </div>
                    {canAssignTechnician && (
                        <div className="col-12">
                            <label className="block mb-2">
                                {t('Technician')} <span style={{ color: 'red' }}>*</span>
                            </label>
                            <Dropdown
                                value={createForm.technicianId}
                                options={technicianOptions}
                                onChange={(e) => setCreateForm({ ...createForm, technicianId: e.value })}
                                placeholder={t('Select technician')}
                                className="w-full"
                                filter
                            />
                        </div>
                    )}
                    {createDialogMode === ORDER_TYPES.REPAIR && (
                        <>
                            <div className="col-12">
                                <label className="block mb-2">
                                    {t('Type of Failure')} <span style={{ color: 'red' }}>*</span>
                                </label>
                                <Dropdown
                                    value={createForm.typeOfFailure}
                                    options={localizedCreateFailureTypeOptions}
                                    onChange={(e) =>
                                        setCreateForm((prev) => ({
                                            ...prev,
                                            typeOfFailure: e.value,
                                            failureCategory: ''
                                        }))
                                    }
                                    placeholder={t('Select failure type')}
                                    className="w-full"
                                    disabled={!createForm.machineId}
                                />
                            </div>
                            <div className="col-12">
                                <label className="block mb-2">
                                    {t('Failure Category')} <span style={{ color: 'red' }}>*</span>
                                </label>
                                <Dropdown
                                    value={createForm.failureCategory}
                                    options={localizedCreateFailureCategoryOptions}
                                    onChange={(e) =>
                                        setCreateForm((prev) => ({
                                            ...prev,
                                            failureCategory: e.value
                                        }))
                                    }
                                    placeholder={t('Select failure category')}
                                    className="w-full"
                                    filter
                                    disabled={!createForm.typeOfFailure}
                                />
                                {selectedCreateFailurePriority && (
                                    <small className="text-700 block mt-2">
                                        {t('Failure Priority')}: <strong>{selectedCreateFailurePriority}/10</strong>
                                    </small>
                                )}
                            </div>
                        </>
                    )}
                    <div className="col-12">
                        <label className="block mb-2">
                            {t('Issue Details')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputTextarea
                            rows={4}
                            value={createForm.issueDetails}
                            onChange={(e) => setCreateForm({ ...createForm, issueDetails: e.target.value })}
                            placeholder={createDialogMode === ORDER_TYPES.FOLLOW_UP ? t('Describe the follow-up visit.') : t('Describe the issue reported by the customer or technician.')}
                            className="w-full"
                        />
                    </div>
                    <div className="col-12">
                        <PhotoUploader
                            label={createDialogMode === ORDER_TYPES.FOLLOW_UP ? t('Follow-up Photo') : t('Issue Photo')}
                            value=""
                            onChange={addCreatePhoto}
                            maxUploadBytes={MAX_CREATE_PHOTO_BYTES}
                            maxDimension={1280}
                            helperText={`${MAX_CREATE_PHOTOS} ${t('photos')}. ${t('Max 1.5 MB each.')}`}
                        />
                        {createForm.photoUrls.length > 0 && (
                            <div className="mt-3">
                                <small className="text-500 block mb-2">
                                    {createForm.photoUrls.length} {createForm.photoUrls.length !== 1 ? t('photos') : t('photo')} {t('added')}
                                </small>
                                <div className="grid">
                                    {createForm.photoUrls.map((photo, index) => (
                                        <div className="col-6" key={`create-photo-${index}`}>
                                            <div style={{ border: '1px solid rgba(148,163,184,0.4)', borderRadius: '6px', padding: '0.5rem' }}>
                                                <img src={photo} alt={`Issue ${index + 1}`} style={{ width: '100%', maxHeight: '120px', objectFit: 'cover', borderRadius: '4px' }} />
                                                <Button
                                                    type="button"
                                                    label={t('Remove')}
                                                    icon="pi pi-times"
                                                    className="p-button-danger p-button-text p-button-sm mt-2"
                                                    onClick={() =>
                                                        setCreateForm((prev) => ({
                                                            ...prev,
                                                            photoUrls: prev.photoUrls.filter((_, i) => i !== index)
                                                        }))
                                                    }
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Dialog>

            <Dialog
                header={selectedOrder?.technician ? t('Reassign Technician') : t('Assign Technician')}
                visible={assignDialogVisible}
                style={{ width: '32rem' }}
                modal
                onHide={() => setAssignDialogVisible(false)}
                footer={renderAssignDialogFooter()}
            >
                <div className="grid p-fluid">
                    <div className="col-12">
                        <label className="block mb-2">{t('Order')}</label>
                        <div className="text-700 font-medium">
                            {selectedOrder?._id ? `#${String(selectedOrder._id).slice(-4).toUpperCase()}` : '-'}
                        </div>
                    </div>
                    <div className="col-12">
                        <label className="block mb-2">{t('Machine')}</label>
                        <div className="text-700 font-medium">
                            {[
                                selectedOrder?.machine?.machine || selectedOrder?.machine?.name || t('Unnamed Machine'),
                                selectedOrder?.machine?.type || t('Unknown Type'),
                                selectedOrder?.machine?.location || t('No location'),
                                selectedOrder?.machine?.serialNumber || selectedOrder?.machine?.deviceId || selectedOrder?.machine?.uuid || t('No ID')
                            ].join(' - ')}
                        </div>
                    </div>
                    <div className="col-12">
                        <label className="block mb-2">
                            {t('Technician')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            value={assignForm.technicianId}
                            options={technicianOptions}
                            onChange={(e) => setAssignForm({ technicianId: e.value })}
                            placeholder={t('Select technician')}
                            className="w-full"
                            filter
                        />
                        {selectedOrder?.technician && (
                            <small className="text-500 block mt-2">
                                {t('Current assignment:')} {technicianBodyTemplate(selectedOrder)}
                            </small>
                        )}
                    </div>
                </div>
            </Dialog>

            <Dialog header={isClosingFollowUp ? t('Close Follow-up') : t('Close Repair Order')} visible={closeDialogVisible} style={{ width: '40rem' }} modal onHide={() => setCloseDialogVisible(false)} footer={renderCloseDialogFooter()}>
                <div className="grid p-fluid">
                    {!isClosingFollowUp && (
                        <>
                            <div className="col-12">
                                <label className="block mb-2">
                                    {t('Type of Failure')} <span style={{ color: 'red' }}>*</span>
                                </label>
                                <Dropdown
                                    value={closeForm.typeOfFailure}
                                    options={localizedFailureTypeOptions}
                                    onChange={(e) =>
                                        setCloseForm((prev) => ({
                                            ...prev,
                                            typeOfFailure: e.value,
                                            failureCategory: ''
                                        }))
                                    }
                                    placeholder={t('Select failure type')}
                                    className="w-full"
                                />
                                {!selectedOrderRepairMachineType && (
                                    <small className="text-500 block mt-2">{t('Select categories from the general list because this ticket has no machine type saved.')}</small>
                                )}
                            </div>
                            <div className="col-12">
                                <label className="block mb-2">
                                    {t('Failure Category')} <span style={{ color: 'red' }}>*</span>
                                </label>
                                <Dropdown
                                    value={closeForm.failureCategory}
                                    options={localizedFailureCategoryOptions}
                                    onChange={(e) =>
                                        setCloseForm((prev) => ({
                                            ...prev,
                                            failureCategory: e.value
                                        }))
                                    }
	                                    placeholder={t('Select failure category')}
	                                    className="w-full"
	                                    filter
	                                />
	                                {selectedFailurePriority && (
	                                    <small className="text-700 block mt-2">
	                                        {t('Failure Priority')}: <strong>{selectedFailurePriority}/10</strong>
	                                    </small>
	                                )}
	                            </div>
                        </>
                    )}
                    <div className="col-12">
                        <label className="block mb-2">
                            {isClosingFollowUp ? t('Follow-up Notes') : t('Actions Taken')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputTextarea
                            rows={4}
                            value={closeForm.actionsTaken}
                            onChange={(e) =>
                                setCloseForm((prev) => ({
                                    ...prev,
                                    actionsTaken: e.target.value
                                }))
                            }
                            placeholder={isClosingFollowUp ? t('Describe the follow-up visit result') : t('Describe the work performed')}
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-6">
                        <label className="block mb-2">
                            {t('Final Status')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            value={closeForm.finalStatus}
                            options={localizedFinalStatusOptions}
                            onChange={(e) =>
                                setCloseForm((prev) => ({
                                    ...prev,
                                    finalStatus: e.value
                                }))
                            }
                            placeholder={t('Select final status')}
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-6">
                        <label className="block mb-2">
                            {isClosingFollowUp ? t('Follow-up Photos') : t('Repair Photos')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <PhotoUploader
                            value=""
                            onChange={addClosePhoto}
                            maxUploadBytes={500 * 1024}
                            maxDimension={1280}
                            helperText={`${MAX_CLOSE_PHOTOS} ${t('photos')}. ${t('Max 500 KB each.')}`}
                        />
                        {closeForm.photoUrls.length > 0 && (
                            <div className="mt-3">
                                <small className="text-500 block mb-2">
                                    {closeForm.photoUrls.length} {closeForm.photoUrls.length !== 1 ? t('photos') : t('photo')} {t('added')}
                                </small>
                                <div className="grid">
                                    {closeForm.photoUrls.map((photo, index) => (
                                        <div className="col-6" key={`close-photo-${index}`}>
                                            <div style={{ border: '1px solid rgba(148,163,184,0.4)', borderRadius: '6px', padding: '0.5rem' }}>
                                                <img src={photo} alt={`Repair ${index + 1}`} style={{ width: '100%', maxHeight: '120px', objectFit: 'cover', borderRadius: '4px' }} />
                                                <Button
                                                    type="button"
                                                    label={t('Remove')}
                                                    icon="pi pi-times"
                                                    className="p-button-danger p-button-text p-button-sm mt-2"
                                                    onClick={() =>
                                                        setCloseForm((prev) => ({
                                                            ...prev,
                                                            photoUrls: prev.photoUrls.filter((_, i) => i !== index)
                                                        }))
                                                    }
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Dialog>

            <div className="grid mb-4">
                <div className={summaryColClass}>
                    <Card>
                        <div className="text-center">
                            <div className="text-2xl font-bold" style={{ color: '#eab308' }}>
                                {totalOpenCount}
                            </div>
                            <div className="text-color-secondary">{translateUiToken('open', preferredLanguage)}</div>
                        </div>
                    </Card>
                </div>
                <div className={summaryColClass}>
                    <Card>
                        <div className="text-center">
                            <div className="text-2xl font-bold" style={{ color: '#16a34a' }}>
                                {monthlyRepairOrderCounts.completed}
                            </div>
                            <div className="text-color-secondary">{t('Completed This Month')}</div>
                        </div>
                    </Card>
                </div>
                <div className={summaryColClass}>
                    <Card>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-primary">{monthlyRepairOrderCounts.total}</div>
                            <div className="text-color-secondary">{t('Total Repair Orders This Month')}</div>
                        </div>
                    </Card>
                </div>
                {!isOperator && (
                    <div className="col-12 md:col-3">
                        <Card>
                            <div className="text-center">
                                <div className="text-2xl font-bold" style={{ color: '#f59e0b' }}>
                                    {unassignedOpenCount}
                                </div>
                                <div className="text-color-secondary">{t('Unassigned Open')}</div>
                            </div>
                        </Card>
                    </div>
                )}
            </div>

            <div className="grid mb-4">
                <div className="col-12 md:col-6">
                    <Card>
                        <div className="text-center">
                            <div className="text-2xl font-bold" style={{ color: '#0ea5e9' }}>
                                {totalOpenFollowUps}
                            </div>
                            <div className="text-color-secondary">{t('Open Follow-ups')}</div>
                        </div>
                    </Card>
                </div>
                <div className="col-12 md:col-6">
                    <Card>
                        <div className="text-center">
                            <div className="text-2xl font-bold" style={{ color: '#14b8a6' }}>
                                {totalClosedFollowUps}
                            </div>
                            <div className="text-color-secondary">{t('Completed Follow-ups')}</div>
                        </div>
                    </Card>
                </div>
            </div>

            {!isOperator && (
                <div className="card mb-4">
                <div className="flex justify-content-between align-items-center mb-3">
                    <h4 className="m-0">{t('Weekly Repair Performance')}</h4>
                </div>
                <div className="grid">
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('From')}</label>
                        <Calendar value={reportRange.from} onChange={(e) => setReportRange({ ...reportRange, from: e.value })} dateFormat="yy-mm-dd" placeholder={t('Start date')} showIcon className="w-full" />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('To')}</label>
                        <Calendar value={reportRange.to} onChange={(e) => setReportRange({ ...reportRange, to: e.value })} dateFormat="yy-mm-dd" placeholder={t('End date')} showIcon className="w-full" />
                    </div>
                    <div className="col-12 md:col-3 flex align-items-end">
                        <Button label={t('Load Weekly Summary')} icon="pi pi-chart-bar" className="p-button-primary w-full" onClick={loadWeeklyReport} />
                    </div>
                </div>

                {weeklySummary && weeklySummary.length > 0 && (
                    <div className="mt-4">
                        <DataTable value={weeklySummary} rows={5} paginator rowsPerPageOptions={[5, 10, 20]}>
                            <Column header={t('Week')} body={(rowData) => `${t('Week')} ${rowData._id.week}, ${rowData._id.year}`} />
                            <Column field="totalRepairs" header={t('Total Repairs')} />
                            <Column field="avgDuration" header={t('Avg Duration')} body={(rowData) => formatTimeFromMinutes(rowData.avgDuration, preferredLanguage)} />
                            <Column field="completed" header={translateUiToken('completed', preferredLanguage)} />
                        </DataTable>
                    </div>
                )}
                </div>
            )}

            {/* Open Tickets Table */}
            <div className="card mb-4">
                <div className="flex justify-content-between align-items-center mb-3">
                    <h4 className="m-0">{t('Open Tickets')}</h4>
                    <div className="flex align-items-center gap-2">
                        {isTechnician && (
                            <>
                                <Button
                                    label={t('My Tickets')}
                                    className={technicianOpenScope === 'mine' ? 'p-button-sm' : 'p-button-sm p-button-outlined'}
                                    onClick={() => {
                                        setTechnicianOpenScope('mine');
                                        setOpenOrdersPagination({ ...openOrdersPagination, page: 1 });
                                    }}
                                />
                                <Button
                                    label={t('Unassigned')}
                                    className={technicianOpenScope === 'unassigned' ? 'p-button-sm p-button-warning' : 'p-button-sm p-button-outlined'}
                                    onClick={() => {
                                        setTechnicianOpenScope('unassigned');
                                        setOpenOrdersPagination({ ...openOrdersPagination, page: 1 });
                                    }}
                                />
                            </>
                        )}
                        <span className="text-500 text-sm">{totalOpenCount} {totalOpenCount !== 1 ? t('open tickets') : t('open ticket')}</span>
                        <Button 
                            label={t('Export to Excel')} 
                            icon="pi pi-file-excel" 
                            className="p-button-success p-button-sm" 
                            onClick={exportOpenOrders}
                            title={t('Export all open tickets to Excel (respects current date filters)')}
                        />
                    </div>
                </div>
                <div className="mb-3">
                    <div className="grid">
                        <div className="col-12 md:col-3">
                            <label className="block mb-2">{t('Filter by Date - From')}</label>
                            <Calendar 
                                value={openOrdersDateRange.from} 
                                onChange={(e) => {
                                    setOpenOrdersDateRange({ ...openOrdersDateRange, from: e.value });
                                    setOpenOrdersPagination({ ...openOrdersPagination, page: 1 });
                                }} 
                                dateFormat="yy-mm-dd" 
                                placeholder={t('Start date')} 
                                showIcon 
                                className="w-full" 
                            />
                        </div>
                        <div className="col-12 md:col-3">
                            <label className="block mb-2">{t('Filter by Date - To')}</label>
                            <Calendar 
                                value={openOrdersDateRange.to} 
                                onChange={(e) => {
                                    setOpenOrdersDateRange({ ...openOrdersDateRange, to: e.value });
                                    setOpenOrdersPagination({ ...openOrdersPagination, page: 1 });
                                }} 
                                dateFormat="yy-mm-dd" 
                                placeholder={t('End date')} 
                                showIcon 
                                className="w-full"
                                minDate={openOrdersDateRange.from}
                            />
                        </div>
                        {(isTechnician || isOperator) && (
                            <div className="col-12 md:col-3">
                                <label className="block mb-2">{t('Filter by Route')}</label>
                                <Dropdown
                                    value={openRouteFilter}
                                    options={localizedRouteOptions}
                                    onChange={(e) => {
                                        setOpenRouteFilter(e.value);
                                        setOpenOrdersPagination({ ...openOrdersPagination, page: 1 });
                                    }}
                                    placeholder={t('All Routes')}
                                    className="w-full"
                                />
                            </div>
                        )}
                        <div className="col-12 md:col-3 flex align-items-end">
                            <Button 
                                label={t('Clear Filter')} 
                                icon="pi pi-times" 
                                className="p-button-secondary w-full" 
                                onClick={() => {
                                    setOpenOrdersDateRange({ from: null, to: null });
                                    setOpenRouteFilter(null);
                                    setOpenOrdersPagination({ ...openOrdersPagination, page: 1 });
                                }} 
                            />
                        </div>
                    </div>
                </div>
                <DataTable 
                    value={openOrders} 
                    paginator 
                    lazy
                    rows={openOrdersPagination.limit} 
                    rowsPerPageOptions={[5, 10, 15, 20]} 
                    emptyMessage={t('No open tickets found.')} 
                    responsiveLayout="scroll" 
                    className="p-datatable-sm"
                    first={(openOrdersPagination.page - 1) * openOrdersPagination.limit}
                    totalRecords={openOrdersPagination.total}
                    sortField={openOrdersSort.sortBy}
                    sortOrder={openOrdersSort.sortOrder === 'asc' ? 1 : -1}
                    onPage={(e) => {
                        const newPage = Math.floor(e.first / e.rows) + 1;
                        loadOpenOrders(newPage, e.rows);
                    }}
                    onSort={handleOpenOrdersSort}
                >
                    <Column header={t('Order')} body={orderCodeBodyTemplate} />
                    <Column header={t('Machine')} body={machineBodyTemplate} />
                    <Column header={t('Created By')} body={createdByBodyTemplate} />
                    <Column header={t('Technician')} body={technicianBodyTemplate} />
                    <Column header={t('Issue Details')} body={issueDetailsBodyTemplate} style={{ minWidth: '22rem' }} />
                    <Column field="failurePriority" header={t('Priority')} body={priorityBodyTemplate} sortable />
                    <Column 
                        field="createdAt" 
                        header={t('Created At')} 
                        body={(rowData) => formatLocalizedDateTime(rowData.createdAt, preferredLanguage)}
                        sortable
                    />
                    <Column header={t('Time Elapsed')} body={timeElapsedBodyTemplate} />
                    <Column header={t('Actions')} body={actionsBodyTemplate} />
                </DataTable>
            </div>

            <div className="card mb-4">
                <div className="flex justify-content-between align-items-center mb-3">
                    <h4 className="m-0">{t('Open Follow-ups')}</h4>
                    <span className="text-500 text-sm">
                        {totalOpenFollowUps} {totalOpenFollowUps !== 1 ? t('open follow-ups') : t('open follow-up')}
                    </span>
                </div>
                <DataTable
                    value={openFollowUps}
                    paginator
                    rows={openFollowUpsPagination.limit}
                    rowsPerPageOptions={[5, 10, 15, 20]}
                    emptyMessage={t('No open follow-ups found.')}
                    responsiveLayout="scroll"
                    className="p-datatable-sm"
                    first={(openFollowUpsPagination.page - 1) * openFollowUpsPagination.limit}
                    totalRecords={openFollowUpsPagination.total}
                    onPage={(e) => {
                        const newPage = Math.floor(e.first / e.rows) + 1;
                        loadOpenFollowUps(newPage, e.rows);
                    }}
                >
                    <Column header={t('Order')} body={orderCodeBodyTemplate} />
                    <Column header={t('Machine')} body={machineBodyTemplate} />
                    <Column header={t('Created By')} body={createdByBodyTemplate} />
                    <Column header={t('Technician')} body={technicianBodyTemplate} />
                    <Column header={t('Follow-up Details')} body={issueDetailsBodyTemplate} style={{ minWidth: '22rem' }} />
                    <Column
                        field="createdAt"
                        header={t('Created At')}
                        body={(rowData) => formatLocalizedDateTime(rowData.createdAt, preferredLanguage)}
                        sortable
                    />
                    <Column header={t('Time Elapsed')} body={timeElapsedBodyTemplate} />
                    <Column header={t('Actions')} body={actionsBodyTemplate} />
                </DataTable>
            </div>

            <div className="card mb-4">
                <div className="flex justify-content-between align-items-center mb-3">
                    <h4 className="m-0">{t('Completed Follow-ups')}</h4>
                    <span className="text-500 text-sm">
                        {totalClosedFollowUps} {totalClosedFollowUps !== 1 ? t('completed follow-ups') : t('completed follow-up')}
                    </span>
                </div>
                <DataTable
                    value={closedFollowUps}
                    paginator
                    rows={closedFollowUpsPagination.limit}
                    rowsPerPageOptions={[5, 10, 15, 20]}
                    emptyMessage={t('No completed follow-ups found.')}
                    responsiveLayout="scroll"
                    className="p-datatable-sm"
                    first={(closedFollowUpsPagination.page - 1) * closedFollowUpsPagination.limit}
                    totalRecords={closedFollowUpsPagination.total}
                    onPage={(e) => {
                        const newPage = Math.floor(e.first / e.rows) + 1;
                        loadClosedFollowUps(newPage, e.rows);
                    }}
                >
                    <Column header={t('Order')} body={orderCodeBodyTemplate} />
                    <Column header={t('Machine')} body={machineBodyTemplate} />
                    <Column header={t('Technician')} body={technicianBodyTemplate} />
                    <Column header={t('Follow-up Details')} body={issueDetailsBodyTemplate} style={{ minWidth: '22rem' }} />
                    <Column field="finalStatus" header={t('Final Status')} body={(rowData) => t(rowData.finalStatus || '-')} />
                    <Column field="timeConsumedMinutes" header={t('Time Consumed')} body={minutesBodyTemplate} />
                    <Column
                        field="closedAt"
                        header={t('Closed At')}
                        body={(rowData) => formatLocalizedDateTime(rowData.closedAt, preferredLanguage)}
                        sortable
                    />
                    <Column header={t('Actions')} body={actionsBodyTemplate} />
                </DataTable>
            </div>

            {/* Closed Orders Table */}
            <div className="card">
                <div className="flex justify-content-between align-items-center mb-3">
                    <h4 className="m-0">{t('Closed Orders')}</h4>
                    <div className="flex align-items-center gap-2">
                        <span className="text-500 text-sm">{totalClosedCount} {totalClosedCount !== 1 ? t('closed orders') : t('closed order')}</span>
                        <Button 
                            label={t('Export to Excel')} 
                            icon="pi pi-file-excel" 
                            className="p-button-success p-button-sm" 
                            onClick={exportClosedOrders}
                            title={t('Export all closed orders to Excel (respects current filters)')}
                        />
                    </div>
                </div>
                <div className="mb-3">
                    <div className="grid">
                        <div className="col-12 md:col-3">
                            <label className="block mb-2">{t('Filter by Date - From')}</label>
                            <Calendar 
                                value={closedOrdersDateRange.from} 
                                onChange={(e) => {
                                    setClosedOrdersDateRange({ ...closedOrdersDateRange, from: e.value });
                                    setClosedOrdersPagination({ ...closedOrdersPagination, page: 1 });
                                }} 
                                dateFormat="yy-mm-dd" 
                                placeholder={t('Start date')} 
                                showIcon 
                                className="w-full" 
                            />
                        </div>
                        <div className="col-12 md:col-3">
                            <label className="block mb-2">{t('Filter by Date - To')}</label>
                            <Calendar 
                                value={closedOrdersDateRange.to} 
                                onChange={(e) => {
                                    setClosedOrdersDateRange({ ...closedOrdersDateRange, to: e.value });
                                    setClosedOrdersPagination({ ...closedOrdersPagination, page: 1 });
                                }} 
                                dateFormat="yy-mm-dd" 
                                placeholder={t('End date')} 
                                showIcon 
                                className="w-full"
                                minDate={closedOrdersDateRange.from}
                            />
                        </div>
                        <div className="col-12 md:col-3">
                            <label className="block mb-2">{t('Filter by Customer')}</label>
                            <Dropdown
                                value={closedCustomerFilter}
                                options={closedCustomerOptions}
                                onChange={(e) => {
                                    setClosedCustomerFilter(e.value);
                                    setClosedOrdersPagination({ ...closedOrdersPagination, page: 1 });
                                }}
                                placeholder={t('Select customer')}
                                className="w-full"
                                filter
                                showClear
                            />
                        </div>
                        <div className="col-12 md:col-3 flex align-items-end">
                            <Button 
                                label={t('Clear Filter')} 
                                icon="pi pi-times" 
                                className="p-button-secondary w-full" 
                                onClick={() => {
                                    setClosedOrdersDateRange({ from: null, to: null });
                                    setClosedCustomerFilter(null);
                                    setClosedOrdersPagination({ ...closedOrdersPagination, page: 1 });
                                }} 
                            />
                        </div>
                    </div>
                </div>
                <DataTable 
                    value={closedOrders} 
                    paginator 
                    rows={closedOrdersPagination.limit} 
                    rowsPerPageOptions={[5, 10, 15, 20]} 
                    emptyMessage={t('No closed orders found.')} 
                    responsiveLayout="scroll" 
                    className="p-datatable-sm"
                    first={(closedOrdersPagination.page - 1) * closedOrdersPagination.limit}
                    totalRecords={closedOrdersPagination.total}
                    onPage={(e) => {
                        const newPage = Math.floor(e.first / e.rows) + 1;
                        loadClosedOrders(newPage, e.rows);
                    }}
                >
                    <Column header={t('Order')} body={orderCodeBodyTemplate} />
                    <Column header={t('Machine')} body={machineBodyTemplate} />
                    <Column header={t('Technician')} body={technicianBodyTemplate} />
                    <Column header={t('Issue Details')} body={issueDetailsBodyTemplate} style={{ minWidth: '22rem' }} />
                    <Column field="typeOfFailure" header={t('Failure Type')} body={(rowData) => t(rowData.typeOfFailure || '-')} />
                    <Column field="failurePriority" header={t('Priority')} body={priorityBodyTemplate} sortable />
                    <Column field="finalStatus" header={t('Final Status')} body={(rowData) => t(rowData.finalStatus || '-')} />
                    <Column field="timeConsumedMinutes" header={t('Time Consumed')} body={minutesBodyTemplate} />
                    <Column 
                        field="closedAt" 
                        header={t('Closed At')} 
                        body={(rowData) => formatLocalizedDateTime(rowData.closedAt, preferredLanguage)}
                        sortable
                    />
                    <Column header={t('Actions')} body={actionsBodyTemplate} />
                </DataTable>
            </div>
        </div>
    );
};

export default RepairOrders;
